// Message.jsx — top-level SMS-sending page.
// Configuration (credentials + sender ID + enabled flag) lives in
// Admin → SMS. This page contains every workflow that actually sends:
//
//   - Test SMS: one-off send to verify config end-to-end.
//   - Roster to one person: pick a person + day/week, sends them their
//     assignments + portal link.
//   - Custom SMS to people: multi-select people, one personalised body.
//   - Bulk by role + date: every referee/bench/coach/court official who
//     worked anything between two dates.
//   - Bulk to participants: filter by program type / age group / name.
//
// Each section has a "Preview recipients" button before the actual send
// where applicable, and a confirmation prompt before charging credits.

import React, { useEffect, useMemo, useState } from 'react';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';
import { dowLong } from '../utils/dayOfWeek';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

function estimateSmsParts(text) {
    if (!text) return { chars: 0, parts: 0 };
    let isUnicode = false;
    for (let i = 0; i < text.length; i++) {
        if (text.charCodeAt(i) > 127) { isUnicode = true; break; }
    }
    const perPart = isUnicode ? 67 : 153;
    const single = isUnicode ? 70 : 160;
    const chars = text.length;
    const parts = chars <= single ? 1 : Math.ceil(chars / perPart);
    return { chars, parts, isUnicode };
}

const sectionStyle = { background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20 };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#444' };
const inputStyle = { padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 14px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer' };
const btnDisabled = { ...btnPrimary, background: '#999', cursor: 'not-allowed' };

// Shared dry-run preview panel for every SMS sender. Shows each
// recipient's name, number and the exact message body (plus the
// auto-appended opt-out line), then a Confirm & send / Cancel step.
// Nothing is sent or charged until Confirm. `preview` is the dryRun
// response: { wouldSend, optOutSuffix, recipients:[{personId,name,to,
// message}], skipped:[{id,name,reason}], error? }.
function SmsPreviewPanel({ preview, loading, onConfirm, onCancel, confirmNoun = 'SMS' }) {
    if (!preview) return null;
    if (preview.error) {
        return (
            <div style={{ marginTop: 10, padding: 10, background: '#ffebee', borderRadius: 4, fontSize: 13 }}>
                Preview failed: {preview.error}
                <div style={{ marginTop: 8 }}>
                    <button onClick={onCancel} style={{ padding: '6px 12px', background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Close</button>
                </div>
            </div>
        );
    }
    const recipients = preview.recipients || [];
    const wouldSend = preview.wouldSend ?? recipients.length;
    return (
        <div style={{ marginTop: 10, border: '1px solid #1565c0', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ background: '#e3f2fd', padding: '8px 10px', fontSize: 13, fontWeight: 600, color: '#1565c0' }}>
                Preview — {wouldSend} message(s) will be sent
                {preview.skipped?.length ? ` · ${preview.skipped.length} skipped (no valid mobile)` : ''}
                <span style={{ fontWeight: 400, color: '#555' }}> · nothing has been sent yet</span>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', background: '#fff' }}>
                {recipients.map(r => (
                    <div key={r.personId ?? r.id ?? r.to} style={{ borderBottom: '1px solid #eee', padding: '8px 10px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name} <span style={{ color: '#888', fontWeight: 400 }}>· {r.to}</span></div>
                        {r.subject && <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>Subject: <span style={{ fontWeight: 400 }}>{r.subject}</span></div>}
                        <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontSize: 12.5, fontFamily: 'inherit', color: '#333' }}>{r.message}{preview.optOutSuffix ? `\n${preview.optOutSuffix}` : ''}</pre>
                    </div>
                ))}
                {recipients.length === 0 && (
                    <div style={{ padding: 10, fontSize: 13, color: '#c62828' }}>No recipients have a valid mobile number.</div>
                )}
                {preview.skipped?.length > 0 && (
                    <div style={{ padding: '8px 10px', fontSize: 12, color: '#e65100', background: '#fff8e1' }}>
                        Skipped: {preview.skipped.map(s => `${s.name || s.id} (${s.reason})`).join(', ')}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '8px 10px', background: '#fafafa' }}>
                <button onClick={onConfirm} disabled={loading || wouldSend === 0} style={(loading || wouldSend === 0) ? btnDisabled : btnPrimary}>
                    {loading ? 'Sending…' : `Confirm & send ${wouldSend} ${preview.channel === 'email' ? (wouldSend === 1 ? 'email' : 'emails') : confirmNoun}`}
                </button>
                <button onClick={onCancel} disabled={loading} style={{ padding: '8px 14px', background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
            </div>
        </div>
    );
}

// Inline "Auto-select" helper for the Roster and Custom sections.
// Looks up the active person IDs that are (or are not) rostered as the
// chosen role in the chosen date range and hands them to the parent via
// onApply(ids). The parent decides how to apply (filter by channel,
// replace vs add, etc.).
function AutoSelectByRoster({ initialStart, initialEnd, controlledStart, controlledEnd, onApply }) {
    const [mode, setMode] = useState('rostered');   // 'rostered' | 'notRostered'
    const [role, setRole] = useState('any');
    // When controlledStart/End are provided (e.g. on the Rosters tab),
    // the date range is owned by the parent and the inputs are hidden —
    // this guarantees the people selected and the roster content
    // produced cover the *same* date range.
    const isControlled = controlledStart != null && controlledEnd != null;
    const [localStart, setLocalStart] = useState(initialStart || new Date().toISOString().split('T')[0]);
    const [localEnd, setLocalEnd] = useState(initialEnd || localStart);
    const start = isControlled ? controlledStart : localStart;
    const end = isControlled ? controlledEnd : localEnd;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastCount, setLastCount] = useState(null);

    async function apply() {
        if (end < start) { setError('End date must be on or after start.'); return; }
        setLoading(true); setError(''); setLastCount(null);
        try {
            const params = new URLSearchParams({ mode, role, startDate: start, endDate: end });
            const res = await auditFetch(`${API_BASE}/people/by-roster-status?${params.toString()}`);
            const data = await res.json();
            if (!res.ok) { setError(data.error || `Failed (HTTP ${res.status})`); return; }
            setLastCount(data.count);
            onApply(data.ids || []);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }

    return (
        <div style={{ marginTop: 8, padding: 8, background: '#f7f9fa', borderRadius: 4, border: '1px solid #e0e0e0' }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#444', marginBottom: 6 }}>Auto-select by roster</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', fontSize: 12 }}>
                <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#666' }}>Who</label>
                    <select value={mode} onChange={e => setMode(e.target.value)} style={{ padding: 4 }}>
                        <option value="rostered">Rostered</option>
                        <option value="notRostered">Not rostered</option>
                        <option value="both">Both (everyone)</option>
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#666' }}>Role</label>
                    <select value={role} onChange={e => setRole(e.target.value)} style={{ padding: 4 }}>
                        <option value="any">Any role</option>
                        <option value="referee">Referee</option>
                        <option value="benchOfficial">Bench official</option>
                        <option value="coach">Coach</option>
                        <option value="courtOfficial">Court official</option>
                    </select>
                </div>
                {!isControlled && (
                    <>
                        <div>
                            <label style={{ display: 'block', fontSize: 11, color: '#666' }}>Start</label>
                            <input type="date" value={localStart} onChange={e => { const v = e.target.value; setLocalStart(v); if (localEnd < v) setLocalEnd(v); }} style={{ padding: 4 }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: 11, color: '#666' }}>End</label>
                            <input type="date" value={localEnd} min={localStart} onChange={e => setLocalEnd(e.target.value)} style={{ padding: 4 }} />
                        </div>
                    </>
                )}
                <button onClick={apply} disabled={loading} style={{ padding: '6px 12px', background: '#1565c0', color: 'white', border: 'none', borderRadius: 4, cursor: loading ? 'wait' : 'pointer', fontWeight: 600 }}>
                    {loading ? 'Loading…' : 'Apply'}
                </button>
                {lastCount != null && !error && (
                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                        Matched {lastCount} {mode === 'both'
                            ? `active ${role === 'any' ? 'people' : `${role}s`}`
                            : `${mode === 'rostered' ? 'rostered' : 'not-rostered'} ${role === 'any' ? 'people' : `${role}s`}`}.
                    </span>
                )}
                {error && <span style={{ color: '#c62828' }}>{error}</span>}
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: '#777' }}>
                {isControlled
                    ? <>Uses the <strong>Start Date</strong> and <strong>End Date</strong> above — the same range each person's roster will cover.</>
                    : <>Replaces your current selection with people matching the filter (only those with the right contact for the chosen channel are kept).</>}
            </div>
        </div>
    );
}

// Small "selected chips" summary above a people checklist. Each chip
// shows a name with an × button to remove just that person from the
// current selection. Renders nothing when nothing is selected.
function SelectedTags({ selected, people, onRemove }) {
    if (!selected || selected.size === 0) return null;
    const byId = new Map(people.map(p => [p.id, p]));
    const rows = Array.from(selected)
        .map(id => byId.get(id))
        .filter(Boolean)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    return (
        <div style={{ marginTop: 8, padding: '6px 8px', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#1565c0', marginBottom: 4 }}>
                {selected.size} selected
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {rows.map(p => (
                    <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 8px', background: '#fff', border: '1px solid #90caf9', borderRadius: 12, fontSize: 12, color: '#0d47a1' }}>
                        {p.name}
                        <button
                            type="button"
                            onClick={() => onRemove(p.id)}
                            title={`Remove ${p.name}`}
                            style={{ border: 'none', background: 'transparent', color: '#1565c0', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px', fontWeight: 700 }}
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
}

// Tabs shown at the top of the Message page. Each tab maps to one of
// the four messaging workflows and respects the same canView permission
// the section already used.
const MESSAGE_TABS = [
    { key: 'roster',       label: 'Rosters', sectionKey: 'send-roster' },
    { key: 'custom',       label: 'Custom Message', sectionKey: 'custom' },
    { key: 'bulk-role',    label: 'Custom Message to Role', sectionKey: 'bulk-role' },
    { key: 'participants', label: 'Custom Message to Participants', sectionKey: 'bulk-participants' },
];

export default function Message() {
    const { canView } = usePermissions();
    const todayStr = new Date().toISOString().split('T')[0];
    const visibleTabs = MESSAGE_TABS.filter(t => canView('message', t.sectionKey));
    const [activeTab, setActiveTab] = useState(visibleTabs[0]?.key || 'roster');
    const weekAheadStr = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    // ===== People + participants caches (fetched once) =====
    const [people, setPeople] = useState([]);
    const [programTypes, setProgramTypes] = useState([]);
    const [ageGroups, setAgeGroups] = useState([]);

    useEffect(() => {
        loadPeople();
        loadProgramTypes();
        loadAgeGroups();
    }, []);

    async function loadPeople() {
        try {
            const res = await auditFetch(`${API_BASE}/person`);
            if (res.ok) setPeople(await res.json());
        } catch {}
    }
    async function loadProgramTypes() {
        try {
            const res = await auditFetch(`${API_BASE}/program-types`);
            if (res.ok) setProgramTypes(await res.json());
        } catch {}
    }
    // Show low-balance warning popup once per send when balanceAfter falls
    // below the configured threshold (returned alongside balanceAfter).
    function maybeWarnLowBalance(data) {
        if (!data || !data.ok || typeof data.balanceAfter !== 'number') return;
        const threshold = typeof data.lowBalanceThreshold === 'number' ? data.lowBalanceThreshold : 50;
        if (data.balanceAfter < threshold) {
            window.alert(`Additional SMS Credits need to be purchased ASAP. Current balance: ${data.balanceAfter}`);
        }
    }
    // Format a "Remaining balance: N credit(s)." suffix when present.
    function balanceSuffix(data) {
        return (data && typeof data.balanceAfter === 'number')
            ? ` Remaining balance: ${data.balanceAfter} credit(s).`
            : '';
    }

    // Channel-aware contact check: SMS needs a phone, Email needs an email.
    const hasContact = (p, channel) => channel === 'email' ? !!p.email : !!p.phoneNumber;
    const contactLabel = (p, channel) => channel === 'email' ? (p.email || 'no email') : (p.phoneNumber || 'no phone');
    // Small inline Email/SMS toggle reused by every section's controls.
    const ChannelSelect = ({ value, onChange }) => (
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
            Channel:
            <select value={value} onChange={(e) => onChange(e.target.value)} style={{ padding: 4 }}>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
            </select>
        </label>
    );

    async function loadAgeGroups() {
        try {
            const res = await auditFetch(`${API_BASE}/participants`);
            if (res.ok) {
                const data = await res.json();
                const set = new Set();
                data.forEach(p => { if (p.ageGroup) set.add(p.ageGroup); });
                setAgeGroups(Array.from(set).sort());
            }
        } catch {}
    }

    // ===== Roster to one or more people =====
    const [rosterChannel, setRosterChannel] = useState('sms');
    const [rosterSearch, setRosterSearch] = useState('');
    const [rosterSelected, setRosterSelected] = useState(new Set());
    const [rosterDate, setRosterDate] = useState(todayStr);
    const [rosterEndDate, setRosterEndDate] = useState(todayStr);
    const [rosterLoading, setRosterLoading] = useState(false);
    const [rosterResult, setRosterResult] = useState(null);
    const [rosterPreview, setRosterPreview] = useState(null);

    const rosterFilteredPeople = useMemo(() => {
        const q = rosterSearch.trim().toLowerCase();
        const list = people.filter(p => !p.isArchived);
        if (!q) return list;
        return list.filter(p => (p.name || '').toLowerCase().includes(q) || (p.phoneNumber || '').includes(q));
    }, [rosterSearch, people]);

    function toggleRosterSelected(id) {
        setRosterSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }
    function selectAllRosterVisible() {
        setRosterSelected(prev => {
            const next = new Set(prev);
            rosterFilteredPeople.forEach(p => { if (hasContact(p, rosterChannel)) next.add(p.id); });
            return next;
        });
    }
    function clearRosterSelection() { setRosterSelected(new Set()); setRosterPreview(null); setRosterResult(null); }

    // Step 1 — build a preview (dryRun). Nothing is sent or charged.
    async function handleRosterPreview() {
        const ids = Array.from(rosterSelected);
        if (ids.length === 0) return;
        if (rosterEndDate < rosterDate) { setRosterResult({ ok: false, error: 'End date must be on or after the start date.' }); return; }
        setRosterLoading(true); setRosterResult(null); setRosterPreview(null);
        try {
            const res = await auditFetch(`${API_BASE}/${rosterChannel}/roster`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ personIds: ids, date: rosterDate, endDate: rosterEndDate, dryRun: true })
            });
            const data = await res.json();
            if (data && data.dryRun) setRosterPreview(data);
            else setRosterResult(data);
        } catch (err) { setRosterResult({ ok: false, error: err.message }); }
        finally { setRosterLoading(false); }
    }

    // Step 2 — actually send (only after a preview has been shown).
    async function handleSendRoster() {
        const ids = Array.from(rosterSelected);
        if (ids.length === 0) return;
        setRosterLoading(true); setRosterResult(null);
        try {
            const res = await auditFetch(`${API_BASE}/${rosterChannel}/roster`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ personIds: ids, date: rosterDate, endDate: rosterEndDate })
            });
            const data = await res.json();
            setRosterResult(data);
            setRosterPreview(null);
            maybeWarnLowBalance(data);
        } catch (err) { setRosterResult({ ok: false, error: err.message }); }
        finally { setRosterLoading(false); }
    }

    // ===== Custom message to multi-selected people =====
    const [customChannel, setCustomChannel] = useState('sms');
    const [customSubject, setCustomSubject] = useState('');
    const [customSearch, setCustomSearch] = useState('');
    const [customSelected, setCustomSelected] = useState(new Set());
    const [customMessage, setCustomMessage] = useState('Hi {first_name}, ');
    const [customLoading, setCustomLoading] = useState(false);
    const [customResult, setCustomResult] = useState(null);
    const [customPreview, setCustomPreview] = useState(null);

    const customFilteredPeople = useMemo(() => {
        const q = customSearch.trim().toLowerCase();
        const list = people.filter(p => !p.isArchived);
        if (!q) return list;
        return list.filter(p => (p.name || '').toLowerCase().includes(q));
    }, [customSearch, people]);

    function toggleCustomSelected(id) {
        setCustomSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }
    function selectAllVisible() {
        setCustomSelected(prev => {
            const next = new Set(prev);
            customFilteredPeople.forEach(p => { if (hasContact(p, customChannel)) next.add(p.id); });
            return next;
        });
    }
    function clearSelection() { setCustomSelected(new Set()); }

    const customNeedsSubject = customChannel === 'email';
    async function handleCustomPreview() {
        const ids = Array.from(customSelected);
        if (ids.length === 0 || !customMessage.trim()) return;
        if (customNeedsSubject && !customSubject.trim()) { setCustomPreview({ error: 'Subject is required for email.' }); return; }
        setCustomLoading(true); setCustomResult(null); setCustomPreview(null);
        try {
            const body = customNeedsSubject
                ? { personIds: ids, subject: customSubject, message: customMessage, dryRun: true }
                : { personIds: ids, message: customMessage, dryRun: true };
            const res = await auditFetch(`${API_BASE}/${customChannel}/custom`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (data && data.dryRun) setCustomPreview(data);
            else setCustomResult(data);
        } catch (err) { setCustomPreview({ error: err.message }); }
        finally { setCustomLoading(false); }
    }
    async function handleSendCustom() {
        const ids = Array.from(customSelected);
        if (ids.length === 0 || !customMessage.trim()) return;
        setCustomLoading(true); setCustomResult(null);
        try {
            const body = customNeedsSubject
                ? { personIds: ids, subject: customSubject, message: customMessage }
                : { personIds: ids, message: customMessage };
            const res = await auditFetch(`${API_BASE}/${customChannel}/custom`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            setCustomResult(data);
            setCustomPreview(null);
            maybeWarnLowBalance(data);
        } catch (err) { setCustomResult({ ok: false, error: err.message }); }
        finally { setCustomLoading(false); }
    }
    const customEst = estimateSmsParts(customMessage);

    // ===== Bulk by role =====
    const [bulkChannel, setBulkChannel] = useState('sms');
    const [bulkSubject, setBulkSubject] = useState('');
    const [bulkRole, setBulkRole] = useState('referee');
    const [bulkStart, setBulkStart] = useState(todayStr);
    const [bulkEnd, setBulkEnd] = useState(weekAheadStr);
    const [bulkMessage, setBulkMessage] = useState('Hi {first_name}, ');
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const bulkEst = estimateSmsParts(bulkMessage);
    const bulkNeedsSubject = bulkChannel === 'email';

    async function runBulkPreview() {
        if (!bulkMessage.trim()) return;
        if (bulkNeedsSubject && !bulkSubject.trim()) { setBulkPreview({ error: 'Subject is required for email.' }); return; }
        setBulkLoading(true); setBulkPreview(null); setBulkResult(null);
        try {
            const body = bulkNeedsSubject
                ? { role: bulkRole, startDate: bulkStart, endDate: bulkEnd, subject: bulkSubject, message: bulkMessage, dryRun: true }
                : { role: bulkRole, startDate: bulkStart, endDate: bulkEnd, message: bulkMessage, dryRun: true };
            const res = await auditFetch(`${API_BASE}/${bulkChannel}/bulk-by-role`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            setBulkPreview(await res.json());
        } catch (err) { setBulkPreview({ error: err.message }); }
        finally { setBulkLoading(false); }
    }
    async function runBulkSend() {
        setBulkLoading(true); setBulkResult(null);
        try {
            const body = bulkNeedsSubject
                ? { role: bulkRole, startDate: bulkStart, endDate: bulkEnd, subject: bulkSubject, message: bulkMessage }
                : { role: bulkRole, startDate: bulkStart, endDate: bulkEnd, message: bulkMessage };
            const res = await auditFetch(`${API_BASE}/${bulkChannel}/bulk-by-role`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            setBulkResult(data);
            setBulkPreview(null);
            maybeWarnLowBalance(data);
        } catch (err) { setBulkResult({ ok: false, error: err.message }); }
        finally { setBulkLoading(false); }
    }

    // ===== Participants =====
    const [partChannel, setPartChannel] = useState('sms');
    const [partSubject, setPartSubject] = useState('');
    const [partProgramIds, setPartProgramIds] = useState([]);
    const [partAgeGroupSel, setPartAgeGroupSel] = useState([]);
    const [partNameSearch, setPartNameSearch] = useState('');
    const [partMessage, setPartMessage] = useState('Hi {contact_name}, ');
    const [partPreview, setPartPreview] = useState(null);
    const [partLoading, setPartLoading] = useState(false);
    const [partResult, setPartResult] = useState(null);
    const partEst = estimateSmsParts(partMessage);
    const partNeedsSubject = partChannel === 'email';

    async function runPartPreview() {
        if (!partMessage.trim()) return;
        if (partNeedsSubject && !partSubject.trim()) { setPartPreview({ error: 'Subject is required for email.' }); return; }
        setPartLoading(true); setPartPreview(null); setPartResult(null);
        try {
            const body = partNeedsSubject
                ? { programIds: partProgramIds, ageGroups: partAgeGroupSel, nameSearch: partNameSearch, subject: partSubject, message: partMessage, dryRun: true }
                : { programIds: partProgramIds, ageGroups: partAgeGroupSel, nameSearch: partNameSearch, message: partMessage, dryRun: true };
            const res = await auditFetch(`${API_BASE}/${partChannel}/participants`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            setPartPreview(await res.json());
        } catch (err) { setPartPreview({ error: err.message }); }
        finally { setPartLoading(false); }
    }
    async function runPartSend() {
        setPartLoading(true); setPartResult(null);
        try {
            const body = partNeedsSubject
                ? { programIds: partProgramIds, ageGroups: partAgeGroupSel, nameSearch: partNameSearch, subject: partSubject, message: partMessage }
                : { programIds: partProgramIds, ageGroups: partAgeGroupSel, nameSearch: partNameSearch, message: partMessage };
            const res = await auditFetch(`${API_BASE}/${partChannel}/participants`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            setPartResult(data);
            setPartPreview(null);
            maybeWarnLowBalance(data);
        } catch (err) { setPartResult({ ok: false, error: err.message }); }
        finally { setPartLoading(false); }
    }

    return (
        <div className="admin-section" style={{ padding: 16 }}>
            <h2 style={{ marginTop: 0 }}>Messaging</h2>
            <p style={{ color: '#666', marginTop: 0, marginBottom: 12 }}>
                Pick a workflow below. Each one lets you choose <strong>SMS</strong> or <strong>Email</strong>, previews every message before sending,
                and uses the matching contact field (mobile for SMS, email for Email). For SMS credentials/sender ID/test sends, see <strong>Admin → SMS</strong>.
            </p>
            {visibleTabs.length > 1 && (
                <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #1565c0', marginBottom: 16, flexWrap: 'wrap' }}>
                    {visibleTabs.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setActiveTab(t.key)}
                            style={{
                                padding: '8px 16px',
                                background: activeTab === t.key ? '#1565c0' : '#f1f5f9',
                                color: activeTab === t.key ? 'white' : '#37474f',
                                border: 'none',
                                borderRadius: '6px 6px 0 0',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: 14,
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}
            {visibleTabs.length === 0 && (
                <div style={{ padding: 12, background: '#ffebee', borderRadius: 6, color: '#c62828', fontSize: 14 }}>
                    You don't have permission to use any of the Messaging workflows. Ask an admin to enable a Messaging section on your security profile.
                </div>
            )}

            {/* SEND ROSTER TO ONE OR MORE PEOPLE */}
            {activeTab === 'roster' && canView('message', 'send-roster') && (
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Send each person their roster</h3>
                        <ChannelSelect value={rosterChannel} onChange={(v) => { setRosterChannel(v); setRosterPreview(null); setRosterResult(null); }} />
                    </div>
                    <p style={{ color: '#666', fontSize: 13, marginTop: 6 }}>
                        Each selected person receives their <em>own</em> roster (their assignments only) plus a link
                        to their My Portal page. Pick the date range, tick everyone you want to send to, then
                        click <strong>Preview</strong> to see the exact message and recipient list before anything is sent.
                        For a single day, set the start and end date to the same day.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Start Date</label>
                            <input type="date" value={rosterDate} onChange={e => { const v = e.target.value; setRosterDate(v); if (rosterEndDate < v) setRosterEndDate(v); }} style={inputStyle} />
                            <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(rosterDate)}</div>
                        </div>
                        <div>
                            <label style={labelStyle}>End Date</label>
                            <input type="date" value={rosterEndDate} min={rosterDate} onChange={e => setRosterEndDate(e.target.value)} style={inputStyle} />
                            <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(rosterEndDate)}</div>
                        </div>
                    </div>
                    <AutoSelectByRoster
                        controlledStart={rosterDate}
                        controlledEnd={rosterEndDate}
                        onApply={(ids) => {
                            const ok = new Set(people.filter(p => hasContact(p, rosterChannel)).map(p => p.id));
                            setRosterSelected(new Set(ids.filter(id => ok.has(id))));
                            setRosterPreview(null); setRosterResult(null);
                        }}
                    />
                    <SelectedTags
                        selected={rosterSelected}
                        people={people}
                        onRemove={(id) => { toggleRosterSelected(id); setRosterPreview(null); setRosterResult(null); }}
                    />
                    <div style={{ marginTop: 12 }}>
                        <label style={labelStyle}>Search</label>
                        <input type="text" value={rosterSearch} onChange={e => setRosterSearch(e.target.value)} placeholder={rosterChannel === 'email' ? 'name or email' : 'name or phone'} style={inputStyle} />
                        <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: 6 }}>
                            {rosterFilteredPeople.map(p => {
                                const ok = hasContact(p, rosterChannel);
                                return (
                                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', cursor: ok ? 'pointer' : 'not-allowed', opacity: ok ? 1 : 0.5, fontSize: 13 }}>
                                        <input
                                            type="checkbox"
                                            checked={rosterSelected.has(p.id)}
                                            onChange={() => ok && toggleRosterSelected(p.id)}
                                            disabled={!ok}
                                        />
                                        <span style={{ flex: 1 }}>{p.name}</span>
                                        <span style={{ color: '#999', fontSize: 11 }}>{contactLabel(p, rosterChannel)}</span>
                                    </label>
                                );
                            })}
                            {rosterFilteredPeople.length === 0 && <div style={{ color: '#999', padding: 8, fontSize: 13 }}>No matches.</div>}
                        </div>
                        <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                            <button onClick={selectAllRosterVisible} style={{ padding: '4px 8px', background: '#1565c0', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Select all visible (with {rosterChannel === 'email' ? 'email' : 'phone'})</button>
                            <button onClick={clearRosterSelection} style={{ padding: '4px 8px', background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
                            <span style={{ color: '#444', marginLeft: 'auto' }}>{rosterSelected.size} selected</span>
                        </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <button onClick={handleRosterPreview} disabled={rosterLoading || rosterSelected.size === 0} style={(rosterLoading || rosterSelected.size === 0) ? btnDisabled : btnPrimary}>
                            {rosterLoading && !rosterPreview ? 'Building preview…' : `Preview roster ${rosterChannel === 'email' ? 'email' : 'SMS'} for ${rosterSelected.size} recipient(s)`}
                        </button>
                    </div>

                    <SmsPreviewPanel
                        preview={rosterPreview}
                        loading={rosterLoading}
                        onConfirm={handleSendRoster}
                        onCancel={() => setRosterPreview(null)}
                    />

                    {rosterResult && (
                        <div style={{ marginTop: 8, padding: 10, background: rosterResult.ok ? '#e8f5e9' : '#ffebee', borderRadius: 4, fontSize: 13 }}>
                            {rosterResult.ok
                                ? `Sent ${rosterResult.recipients ?? rosterResult.delivered ?? 0} message(s).${typeof rosterResult.totalCost === 'number' ? ` Cost: ${rosterResult.totalCost} credit(s).` : ''}${rosterResult.skipped?.length ? ` Skipped ${rosterResult.skipped.length}.` : ''}${balanceSuffix(rosterResult)}`
                                : `Failed: ${rosterResult.error || 'unknown error'}`}
                        </div>
                    )}
                </div>
            )}

            {/* CUSTOM MESSAGE TO MULTIPLE PEOPLE */}
            {activeTab === 'custom' && canView('message', 'custom') && (
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Custom message to selected people</h3>
                        <ChannelSelect value={customChannel} onChange={(v) => { setCustomChannel(v); setCustomPreview(null); setCustomResult(null); }} />
                    </div>
                    <p style={{ color: '#666', fontSize: 13, marginTop: 6 }}>
                        Pick people from the list, then write one message. Personalise with <code>{'{first_name}'}</code> or <code>{'{name}'}</code>.
                        {customChannel === 'sms' && <> <code>{'{optout}'}</code> is appended automatically.</>}
                        {customChannel === 'email' && <> A <strong>Subject</strong> is required for email.</>}
                    </p>
                    <AutoSelectByRoster
                        onApply={(ids) => {
                            const ok = new Set(people.filter(p => hasContact(p, customChannel)).map(p => p.id));
                            setCustomSelected(new Set(ids.filter(id => ok.has(id))));
                            setCustomPreview(null); setCustomResult(null);
                        }}
                    />
                    <SelectedTags
                        selected={customSelected}
                        people={people}
                        onRemove={(id) => { toggleCustomSelected(id); setCustomPreview(null); setCustomResult(null); }}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                        <div>
                            <label style={labelStyle}>Search</label>
                            <input type="text" value={customSearch} onChange={e => setCustomSearch(e.target.value)} placeholder="name" style={inputStyle} />
                            <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: 6 }}>
                                {customFilteredPeople.map(p => {
                                    const ok = hasContact(p, customChannel);
                                    return (
                                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', cursor: ok ? 'pointer' : 'not-allowed', opacity: ok ? 1 : 0.5, fontSize: 13 }}>
                                            <input
                                                type="checkbox"
                                                checked={customSelected.has(p.id)}
                                                onChange={() => ok && toggleCustomSelected(p.id)}
                                                disabled={!ok}
                                            />
                                            <span style={{ flex: 1 }}>{p.name}</span>
                                            <span style={{ color: '#999', fontSize: 11 }}>{contactLabel(p, customChannel)}</span>
                                        </label>
                                    );
                                })}
                                {customFilteredPeople.length === 0 && <div style={{ color: '#999', padding: 8, fontSize: 13 }}>No matches.</div>}
                            </div>
                            <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                                <button onClick={selectAllVisible} style={{ padding: '4px 8px', background: '#1565c0', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Select all visible (with {customChannel === 'email' ? 'email' : 'phone'})</button>
                                <button onClick={clearSelection} style={{ padding: '4px 8px', background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
                                <span style={{ color: '#444', marginLeft: 'auto' }}>{customSelected.size} selected</span>
                            </div>
                        </div>
                        <div>
                            {customChannel === 'email' && (
                                <>
                                    <label style={labelStyle}>Subject</label>
                                    <input type="text" value={customSubject} onChange={e => setCustomSubject(e.target.value)} style={inputStyle} placeholder="Subject (required for email)" />
                                </>
                            )}
                            <label style={labelStyle}>Message</label>
                            <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)} style={{ ...inputStyle, minHeight: 220, fontFamily: 'inherit' }} />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {customChannel === 'sms'
                                    ? `${customEst.chars} chars / ${customEst.parts} part(s) per recipient ${customEst.isUnicode ? '(unicode)' : '(GSM-7)'}`
                                    : `${customMessage.length} chars · plain-text email body`}
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <button onClick={handleCustomPreview} disabled={customLoading || customSelected.size === 0 || !customMessage.trim() || (customNeedsSubject && !customSubject.trim())} style={(customLoading || customSelected.size === 0 || !customMessage.trim() || (customNeedsSubject && !customSubject.trim())) ? btnDisabled : btnPrimary}>
                            {customLoading && !customPreview ? 'Building preview…' : `Preview ${customChannel === 'email' ? 'email' : 'SMS'} for ${customSelected.size} recipient(s)`}
                        </button>
                    </div>
                    <SmsPreviewPanel
                        preview={customPreview}
                        loading={customLoading}
                        onConfirm={handleSendCustom}
                        onCancel={() => setCustomPreview(null)}
                    />
                    {customResult && (
                        <div style={{ marginTop: 8, padding: 10, background: customResult.ok ? '#e8f5e9' : '#ffebee', borderRadius: 4, fontSize: 13 }}>
                            {customResult.ok
                                ? `Sent ${customResult.recipients ?? customResult.delivered ?? 0} message(s).${typeof customResult.totalCost === 'number' ? ` Cost: ${customResult.totalCost} credit(s).` : ''}${customResult.skipped?.length ? ` Skipped ${customResult.skipped.length}.` : ''}${balanceSuffix(customResult)}`
                                : `Failed: ${customResult.error || 'unknown error'}`}
                        </div>
                    )}
                </div>
            )}

            {/* BULK BY ROLE */}
            {activeTab === 'bulk-role' && canView('message', 'bulk-role') && (
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Bulk by role and date range</h3>
                        <ChannelSelect value={bulkChannel} onChange={(v) => { setBulkChannel(v); setBulkPreview(null); setBulkResult(null); }} />
                    </div>
                    <p style={{ color: '#666', fontSize: 13, marginTop: 6 }}>
                        Sends to every person assigned in the chosen role within the date range.
                        Personalise with <code>{'{first_name}'}</code> or <code>{'{name}'}</code>.
                        {bulkChannel === 'email' && <> A <strong>Subject</strong> is required for email.</>}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Role</label>
                            <select value={bulkRole} onChange={e => setBulkRole(e.target.value)} style={inputStyle}>
                                <option value="referee">Referees</option>
                                <option value="benchOfficial">Bench Officials</option>
                                <option value="coach">Coaches</option>
                                <option value="courtOfficial">Court Officials</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Start Date</label>
                            <input type="date" value={bulkStart} onChange={e => setBulkStart(e.target.value)} style={inputStyle} />
                            <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(bulkStart)}</div>
                        </div>
                        <div>
                            <label style={labelStyle}>End Date</label>
                            <input type="date" value={bulkEnd} onChange={e => setBulkEnd(e.target.value)} style={inputStyle} />
                            <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(bulkEnd)}</div>
                        </div>
                    </div>
                    {bulkChannel === 'email' && (
                        <div style={{ marginTop: 12 }}>
                            <label style={labelStyle}>Subject</label>
                            <input type="text" value={bulkSubject} onChange={e => setBulkSubject(e.target.value)} style={inputStyle} placeholder="Subject (required for email)" />
                        </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                        <label style={labelStyle}>Message</label>
                        <textarea value={bulkMessage} onChange={e => setBulkMessage(e.target.value)} style={{ ...inputStyle, minHeight: 80, fontFamily: 'inherit' }} />
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            {bulkChannel === 'sms'
                                ? `${bulkEst.chars} chars / ${bulkEst.parts} part(s) per recipient ${bulkEst.isUnicode ? '(unicode)' : '(GSM-7)'}`
                                : `${bulkMessage.length} chars · plain-text email body`}
                        </div>
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button onClick={runBulkPreview} disabled={bulkLoading || !bulkMessage.trim() || (bulkNeedsSubject && !bulkSubject.trim())} style={(bulkLoading || !bulkMessage.trim() || (bulkNeedsSubject && !bulkSubject.trim())) ? btnDisabled : btnPrimary}>
                            {bulkLoading && !bulkPreview ? 'Building preview…' : 'Preview recipients & message'}
                        </button>
                    </div>
                    <SmsPreviewPanel
                        preview={bulkPreview}
                        loading={bulkLoading}
                        onConfirm={runBulkSend}
                        onCancel={() => setBulkPreview(null)}
                    />
                    {bulkResult && (
                        <div style={{ marginTop: 12, padding: 10, background: bulkResult.ok ? '#e8f5e9' : '#ffebee', borderRadius: 4, fontSize: 13 }}>
                            {bulkResult.ok
                                ? `Sent ${bulkResult.recipients ?? bulkResult.delivered ?? 0} message(s).${typeof bulkResult.totalCost === 'number' ? ` Cost: ${bulkResult.totalCost} credit(s).` : ''}${bulkResult.skipped?.length ? ` Skipped ${bulkResult.skipped.length}.` : ''}${balanceSuffix(bulkResult)}`
                                : `Failed: ${bulkResult.error || 'unknown error'}`}
                        </div>
                    )}
                </div>
            )}

            {/* PARTICIPANTS */}
            {activeTab === 'participants' && canView('message', 'bulk-participants') && (
                <div style={sectionStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Bulk — Participants</h3>
                        <ChannelSelect value={partChannel} onChange={(v) => { setPartChannel(v); setPartPreview(null); setPartResult(null); }} />
                    </div>
                    <p style={{ color: '#666', fontSize: 13, marginTop: 6 }}>
                        Sends to the contact {partChannel === 'email' ? 'email' : 'mobile'} on file for each matching participant.
                        Personalise with <code>{'{contact_name}'}</code>, <code>{'{first_name}'}</code>, or <code>{'{name}'}</code>.
                        {partChannel === 'email' && <> A <strong>Subject</strong> is required for email.</>}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Program Types</label>
                            <select multiple value={partProgramIds.map(String)} onChange={e => setPartProgramIds(Array.from(e.target.selectedOptions, o => parseInt(o.value, 10)))} style={{ ...inputStyle, minHeight: 90 }}>
                                {programTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Age Groups</label>
                            <select multiple value={partAgeGroupSel} onChange={e => setPartAgeGroupSel(Array.from(e.target.selectedOptions, o => o.value))} style={{ ...inputStyle, minHeight: 90 }}>
                                {ageGroups.map(ag => <option key={ag} value={ag}>{ag}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <label style={labelStyle}>Name search (optional)</label>
                        <input type="text" value={partNameSearch} onChange={e => setPartNameSearch(e.target.value)} placeholder="e.g. jones" style={inputStyle} />
                    </div>
                    {partChannel === 'email' && (
                        <div style={{ marginTop: 12 }}>
                            <label style={labelStyle}>Subject</label>
                            <input type="text" value={partSubject} onChange={e => setPartSubject(e.target.value)} style={inputStyle} placeholder="Subject (required for email)" />
                        </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                        <label style={labelStyle}>Message</label>
                        <textarea value={partMessage} onChange={e => setPartMessage(e.target.value)} style={{ ...inputStyle, minHeight: 80, fontFamily: 'inherit' }} />
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            {partChannel === 'sms'
                                ? `${partEst.chars} chars / ${partEst.parts} part(s) per recipient ${partEst.isUnicode ? '(unicode)' : '(GSM-7)'}`
                                : `${partMessage.length} chars · plain-text email body`}
                        </div>
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button onClick={runPartPreview} disabled={partLoading || !partMessage.trim() || (partNeedsSubject && !partSubject.trim())} style={(partLoading || !partMessage.trim() || (partNeedsSubject && !partSubject.trim())) ? btnDisabled : btnPrimary}>
                            {partLoading && !partPreview ? 'Building preview…' : 'Preview recipients & message'}
                        </button>
                    </div>
                    <SmsPreviewPanel
                        preview={partPreview}
                        loading={partLoading}
                        onConfirm={runPartSend}
                        onCancel={() => setPartPreview(null)}
                        confirmNoun="SMS"
                    />
                    {partResult && (
                        <div style={{ marginTop: 12, padding: 10, background: partResult.ok ? '#e8f5e9' : '#ffebee', borderRadius: 4, fontSize: 13 }}>
                            {partResult.ok
                                ? `Sent ${partResult.recipients ?? partResult.delivered ?? 0} message(s).${typeof partResult.totalCost === 'number' ? ` Cost: ${partResult.totalCost} credit(s).` : ''}${partResult.skipped?.length ? ` Skipped ${partResult.skipped.length}.` : ''}${balanceSuffix(partResult)}`
                                : `Failed: ${partResult.error || 'unknown error'}`}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
